import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AttendanceSettingCreateVersionDocument,
  SettingPageEmployeesSettingFragment,
} from 'generated/resources/graphql'
import moment from 'moment'
import type { $Values } from 'utility-types'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateToString } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EffectiveAtDisclaimerModal from 'modules/attendance/components/SettingItemPage/TimeTracking/EffectiveAtDisclaimerModal'
import { AmplitudeProps } from 'modules/attendance/lib/types'
import attendanceSettings from 'modules/attendance/store/collections/settings'
import AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

import Option from './_Option'

const MODAL_OPTIONS = {
  CREATE: 'create',
  UPDATE: 'update',
}
type ModalOptions = $Values<typeof MODAL_OPTIONS>

interface Props {
  onCreate: (setting: SettingPageEmployeesSettingFragment) => void
  onUpdate: () => void
  onClose: () => void
  running: boolean
  attendanceSettingId: number
  attendanceSettingEffectiveAt: string
  origin?: string
}

const ManageSettingVersionModal = ({
  onCreate,
  onUpdate,
  onClose,
  running = false,
  attendanceSettingId,
  attendanceSettingEffectiveAt,
  origin,
}: Props) => {
  const [openEffectiveAtDisclaimerModal, setOpenEffectiveAtDisclaimerModal] = React.useState(false)
  const [option, setOption] = React.useState<ModalOptions | null>(null)
  const [date, setDate] = React.useState<moment.Moment>(moment())
  const selectedDate = dateToString(date, 'backend')
  const isCreate = option === MODAL_OPTIONS.CREATE
  const isUpdate = option === MODAL_OPTIONS.UPDATE

  const [createVersion] = useMutation(AttendanceSettingCreateVersionDocument, {
    extractorFn: (data) => data?.attendanceMutations.createVersionAttendanceEmployeesSetting,
    onCompleted: (data) => {
      const employeesSetting =
        data?.attendanceMutations.createVersionAttendanceEmployeesSetting.employeesSetting

      if (employeesSetting) {
        const newSetting = AttendanceSetting.fromFragment(employeesSetting)
        attendanceSettings.add(newSetting)

        onCreate(employeesSetting)
      }
    },
  })

  const { running: submitting, performAction: createNewVersion } = useAsyncAction(
    async () => {
      setOpenEffectiveAtDisclaimerModal(false)

      const result = await createVersion({
        variables: {
          id: +attendanceSettingId,
          effectiveAt: selectedDate,
        },
      })

      const handled = handleResult(result, () => {})

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('time_tracking.settings.versions.manage_modal.success_message'),
        })
      } else {
        const errorMessages = handled.errors
          .map((e) => ('messages' in e ? e.messages.join(', ') : e.message))
          .join('; ')

        app.setFlash({
          type: 'wrong',
          message: errorMessages,
        })
      }
    },
    {
      successFlash: false,
    }
  )

  const handleOptionSelect = (modalOption: ModalOptions) => {
    setOption(modalOption)
    if (origin && origin === AmplitudeProps.Origin.ComplementaryHours) {
      // We only want to track the policy changes for the complementary hours
      track(trackingEvents.timeTracking.selectPolicyChanges, {
        selected: modalOption,
        origin,
      })
    }
  }

  const onClickSubmit = () => {
    switch (option) {
      case MODAL_OPTIONS.CREATE:
        if (attendanceSettingEffectiveAt === selectedDate) createNewVersion()
        else setOpenEffectiveAtDisclaimerModal(true)
        break

      case MODAL_OPTIONS.UPDATE:
        onUpdate()
        break

      default:
        return null
    }
  }

  return (
    <>
      {openEffectiveAtDisclaimerModal ? (
        <EffectiveAtDisclaimerModal
          onConfirm={createNewVersion}
          onClose={() => setOpenEffectiveAtDisclaimerModal(false)}
          newVersion
        />
      ) : (
        <Modal
          illustration="alert"
          title={i18n.t('time_tracking.settings.versions.manage_modal.title')}
          description={i18n.t('time_tracking.settings.versions.manage_modal.description')}
          onSubmit={onClickSubmit}
          onClose={onClose}
          footer={
            <>
              <Button type="secondary" onClick={onClose}>
                {i18n.t('common.cancel')}
              </Button>
              <Button disabled={running || submitting || !option} submit>
                {i18n.t('common.save_changes')}
              </Button>
            </>
          }
        >
          <Box gap="s16">
            <Option
              isSelected={isCreate}
              title={i18n.t('time_tracking.settings.versions.manage_modal.options.create')}
              description={i18n.t(
                'time_tracking.settings.versions.manage_modal.options.create_description'
              )}
              onClick={() => handleOptionSelect(MODAL_OPTIONS.CREATE)}
              date={date}
              setDate={setDate}
            />
            <Option
              isSelected={isUpdate}
              title={i18n.t('time_tracking.settings.versions.manage_modal.options.update')}
              description={i18n.t(
                'time_tracking.settings.versions.manage_modal.options.update_description'
              )}
              onClick={() => handleOptionSelect(MODAL_OPTIONS.UPDATE)}
            />
          </Box>
        </Modal>
      )}
    </>
  )
}

export default observer(ManageSettingVersionModal)
